import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { debounce } from "lodash";

import { dispatch } from "../../../state/store";
import { setOriginalAppLink, setOriginalAppName, setSelectedApp } from "../../../state/ducks";

import { appsService, appUserService, notificationService, t } from "../../../services";
import { responseUtil } from "../../../utils";
import { alignItems, appManageTypes, appUserRoles, colors, strCode } from "../../../consts";

import {
  FlexContainers,
  IconButton,
  Input,
  Paragraph2,
  SVG,
  Switcher,
  WrapperContainer
} from "../../../components";
import EditButtonsGroup from "../../../components/Buttons/EditButtonsGroup";

const GeneralSettings = ({ isUpdate }) => {
  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const manageType = useSelector((store) => store.apps.manageType);
  const originalAppName = useSelector((store) => store.apps.originalAppName);
  const originalAppLink = useSelector((store) => store.apps.originalAppLink);
  const isEdit = manageType === appManageTypes.EDIT || isUpdate;

  const [name, setName] = useState(selectedApp?.name ?? "");
  const [link, setLink] = useState(selectedApp?.link ?? "");
  const [active, setActive] = useState(!selectedApp?.isDisabled ?? true);
  const [isNameDisabled, setIsNameDisabled] = useState(isEdit);
  const [isLinkDisabled, setIsLinkDisabled] = useState(isEdit);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsNameDisabled(isEdit);
    setIsLinkDisabled(isEdit);

    dispatch(setOriginalAppName(selectedApp?.name));
    dispatch(setOriginalAppLink(selectedApp?.link));

    if (isEdit) {
      if (!name) {
        setName(selectedApp?.name);
      }

      if (!link) {
        setLink(selectedApp?.link);
      }
    }
  }, [isEdit]);

  useEffect(() => {
    dispatch(setSelectedApp({ isDisabled: !active }));
  }, [active]);

  const debouncedSaveAppName = useCallback(
    debounce((value) => {
      dispatch(setSelectedApp({ name: value }));
      dispatch(setSelectedApp({ appType: value }));
    }, 500),
    [dispatch]
  );

  const debouncedSaveAppUrl = useCallback(
    debounce((value) => {
      dispatch(setSelectedApp({ link: value }));
    }, 500),
    [dispatch]
  );

  const handleAppNameChange = (value) => {
    setName(value);

    if (!isEdit) {
      debouncedSaveAppName(value);
    }
  };

  const handleAppUrlChange = (value) => {
    setLink(value);
    if (!isEdit) {
      debouncedSaveAppUrl(value);
    }
  };

  const onCancelEdit = (field) => {
    if (field === "name") {
      setName(originalAppName);
      dispatch(setSelectedApp({ name: originalAppName }));
      setIsNameDisabled(true);
    } else if (field === "link") {
      setLink(originalAppLink);
      dispatch(setSelectedApp({ link: originalAppLink }));
      setIsLinkDisabled(true);
    }
  };

  const onSave = async (field) => {
    setLoading(true);

    const updatedData = field === "name" ? { name } : { name, link };
    const res = await appsService.updateApp({
      appType: selectedApp.appType,
      ...updatedData
    });

    if (responseUtil.isResponseSuccessful(res)) {
      notificationService.showSuccess({
        title: t(strCode.APP_UPDATED_SHORT_TITLTE),
        message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name }),
        immediate: false
      });

      dispatch(setSelectedApp(updatedData));
      field === "name" ? setIsNameDisabled(true) : setIsLinkDisabled(true);
      field === "name" ? dispatch(setOriginalAppName(name)) : dispatch(setOriginalAppLink(link));
    }

    setLoading(false);
  };

  const isEditAvailable = useMemo(() => {
    return appUserService.isAdmin() || appUserService.hasRole(appUserRoles.ApplicationManager);
  }, []);

  return (
    <WrapperContainer width={"100%"}>
      <WrapperContainer
        Container={FlexContainers.FlexRow}
        alignItems={alignItems.end}
        width={"100%"}
        gap={8}
        mb={24}>
        <WrapperContainer
          Container={FlexContainers.FlexRowStart}
          gap={isEdit ? (!isNameDisabled ? 24 : 64) : 16}
          width={"100%"}>
          <WrapperContainer
            Container={FlexContainers.FlexRowStart}
            width={"100%"}
            alignItems={alignItems.end}
            gap={8}>
            <Input
              id={"app-name"}
              title={t(strCode.APPLICATION_NAME_TITLE)}
              value={name}
              setValue={handleAppNameChange}
              disabled={(isEdit ? isNameDisabled : false) || loading}
              placeholder={t(strCode.APPLICATION_NAME_TITLE)}
            />

            {isEdit && isEditAvailable && (
              <EditButtonsGroup
                handleEdit={() => setIsNameDisabled(false)}
                isEdit={isNameDisabled}
                isLoading={loading}
                handleCancel={() => onCancelEdit("name")}
                handleAccept={() => name !== originalAppName && name && onSave("name")}
                disableAccept={!name || name === originalAppName}
              />
            )}
          </WrapperContainer>

          <WrapperContainer
            Container={FlexContainers.FlexRowStart}
            width={"100%"}
            alignItems={alignItems.end}
            gap={8}>
            <Input
              id={"app-link"}
              title={t(strCode.LINK_TO_APP_TITLE)}
              value={link}
              setValue={handleAppUrlChange}
              disabled={(isEdit ? isLinkDisabled : false) || loading}
              placeholder={t(strCode.LINK_TO_APP_PLACEHOLDER)}
            />

            {isEdit && isEditAvailable && (
              <EditButtonsGroup
                handleEdit={() => setIsLinkDisabled(false)}
                isEdit={isLinkDisabled}
                isLoading={loading}
                handleCancel={() => onCancelEdit("link")}
                handleAccept={() => onSave("link")}
                disableAccept={link === originalAppLink}
              />
            )}
          </WrapperContainer>
        </WrapperContainer>

        {isEdit && (
          <a rel="noreferrer" href={link} target="_blank">
            <IconButton hasHoverCircle isLarge tooltipLabel={t(strCode.OPEN_APP_TITLE)}>
              <SVG.NavigateToAppLinkIcon />
            </IconButton>
          </a>
        )}
      </WrapperContainer>

      <WrapperContainer Container={FlexContainers.FlexRow} gap={64} mr={48} mb={24}>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} width={"100%"}>
          <WrapperContainer
            Container={FlexContainers.FlexColumnStart}
            alignItems={alignItems.start}>
            <Paragraph2 style={{ fontWeight: "600" }}>{t(strCode.ACTIVE_APP_TITLE)}</Paragraph2>
            <Paragraph2 style={{ color: colors.RAISIN_70 }}>
              {t(strCode.ACTIVE_APP_DESCRIPTION)}
            </Paragraph2>
          </WrapperContainer>
          <WrapperContainer pr={56}>
            <Switcher checked={active} onSwitch={() => setActive((prev) => !prev)} />
          </WrapperContainer>
        </WrapperContainer>
        <WrapperContainer Container={FlexContainers.FlexRowBetween} width={"100%"}>
          {/* MFA required here*/}
        </WrapperContainer>
      </WrapperContainer>
    </WrapperContainer>
  );
};

GeneralSettings.propTypes = {
  isUpdate: PropTypes.number
};

export default GeneralSettings;
